import { useState, useEffect, useRef, useCallback } from 'react';
import { CreditCard, Lock, AlertCircle, ShieldCheck } from 'lucide-react';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Authorization Hold thresholds (must match backend)
const AUTH_HOLD_MIN_THRESHOLD = 5000;
const AUTH_HOLD_MAX_THRESHOLD = 15000;

export default function SquarePaymentForm({ 
  amount, 
  email, 
  verificationToken,
  orderData,
  onSuccess, 
  onError,
  disabled,
  forceDirectCharge = false  // For balance payments, always use direct charge
}) {
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [card, setCard] = useState(null);
  const cardContainerRef = useRef(null);
  const paymentsRef = useRef(null);
  const initAttempted = useRef(false);
  
  // Determine if this payment requires authorization hold
  // Skip auth hold if forceDirectCharge is true (for balance payments)
  const amountNum = parseFloat(amount) || 0;
  const requiresAuthHold = !forceDirectCharge && amountNum >= AUTH_HOLD_MIN_THRESHOLD && amountNum < AUTH_HOLD_MAX_THRESHOLD;

  // Initialize Square when component mounts
  const initSquare = useCallback(async () => {
    if (initAttempted.current) return;
    initAttempted.current = true;

    console.log('[Square] Starting initialization...');

    try {
      // Get Square config from backend
      console.log('[Square] Fetching config from backend...');
      const res = await fetch(`${BACKEND_URL}/api/payments/square/config`);
      if (!res.ok) throw new Error('Failed to get Square config');
      const config = await res.json();
      console.log('[Square] Config received:', config);

      // Wait for Square SDK to load with timeout
      console.log('[Square] Waiting for SDK to load...');
      let attempts = 0;
      while (!window.Square && attempts < 50) {
        await new Promise(resolve => setTimeout(resolve, 200));
        attempts++;
        if (attempts % 10 === 0) {
          console.log(`[Square] Still waiting for SDK... attempt ${attempts}`);
        }
      }
      
      if (!window.Square) {
        console.error('[Square] SDK failed to load after 10 seconds');
        throw new Error('Square payment system is unavailable. Please use Wire Transfer or Check payment instead.');
      }
      console.log('[Square] SDK loaded successfully');

      // Initialize Square payments
      console.log('[Square] Initializing payments with app:', config.applicationId);
      const payments = window.Square.payments(config.applicationId, config.locationId);
      paymentsRef.current = payments;
      console.log('[Square] Payments object created');

      // Create card payment method
      console.log('[Square] Creating card instance...');
      const cardInstance = await payments.card();
      console.log('[Square] Card instance created');
      
      // Wait for the container to be in the DOM (with retries)
      let container = null;
      for (let i = 0; i < 20; i++) {
        container = document.getElementById('square-card-container');
        if (container) break;
        console.log(`[Square] Waiting for container... attempt ${i + 1}`);
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      if (!container) {
        throw new Error('Payment form container not found. Please refresh the page.');
      }
      console.log('[Square] Container found, attaching card...');
      
      await cardInstance.attach('#square-card-container');
      console.log('[Square] Card attached successfully!');
      setCard(cardInstance);
      setLoading(false);
    } catch (err) {
      console.error('[Square] Initialization error:', err);
      setError(err.message || 'Failed to load payment form. Please try Wire Transfer or Check payment.');
      setLoading(false);
    }
  }, []);

  // Start initialization after a brief delay to ensure DOM is ready
  useEffect(() => {
    const timer = setTimeout(() => {
      initSquare();
    }, 100);
    return () => clearTimeout(timer);
  }, [initSquare]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (card) {
        try {
          card.destroy();
        } catch (e) {
          console.warn('Error destroying card:', e);
        }
      }
    };
  }, [card]);

  const handlePayment = async () => {
    if (!card || processing || disabled) return;

    setProcessing(true);
    setError(null);

    try {
      // Tokenize the card
      const tokenResult = await card.tokenize();
      
      if (tokenResult.status !== 'OK') {
        throw new Error(tokenResult.errors?.[0]?.message || 'Card tokenization failed');
      }

      // Verify buyer with 3D Secure if available
      let verificationTokenFrom3DS = null;
      if (paymentsRef.current && tokenResult.token) {
        try {
          const verifyResult = await paymentsRef.current.verifyBuyer(
            tokenResult.token,
            {
              amount: amount.toString(),
              billingContact: { email },
              currencyCode: 'USD',
              intent: requiresAuthHold ? 'STORE' : 'CHARGE'  // STORE for auth hold, CHARGE for immediate
            }
          );
          verificationTokenFrom3DS = verifyResult?.token;
        } catch (verifyErr) {
          console.warn('3D Secure verification failed, proceeding without:', verifyErr);
        }
      }

      // Use authorization endpoint for $5k-$15k orders
      const endpoint = requiresAuthHold 
        ? `${BACKEND_URL}/api/payments/square/create-authorization`
        : `${BACKEND_URL}/api/payments/square/create-payment`;

      // Send payment to backend
      const paymentRes = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source_id: tokenResult.token,
          amount: amount,
          email: email,
          verification_token: verificationToken,
          verification_token_from_3ds: verificationTokenFrom3DS,
          order_data: orderData,
          idempotency_key: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        })
      });

      const paymentData = await paymentRes.json();

      if (!paymentRes.ok) {
        throw new Error(paymentData.detail || 'Payment failed');
      }

      // Success!
      onSuccess?.(paymentData);
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message);
      onError?.(err.message);
    } finally {
      setProcessing(false);
    }
  };

  // Error state - show error with retry option
  if (error && !card) {
    return (
      <div className="space-y-4">
        <div className="p-6 border-2 border-red-200 rounded-xl bg-red-50">
          <div className="flex items-center gap-3 text-red-700 mb-3">
            <AlertCircle className="w-5 h-5" />
            <span>Failed to load payment form: {error}</span>
          </div>
          <button 
            onClick={() => window.location.reload()}
            className="w-full py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg text-sm font-medium"
          >
            Refresh Page & Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Card Input Container - Always render so Square can attach */}
      <div className="p-4 border-2 border-gray-200 rounded-xl bg-white">
        <div className="flex items-center gap-2 mb-3">
          <CreditCard className="w-5 h-5 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Card Details</span>
        </div>
        
        {/* Loading state shown above the container */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-4 gap-2 mb-2">
            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
            <span className="text-gray-500 text-sm">Loading secure payment form...</span>
            <button 
              onClick={() => window.location.reload()}
              className="text-xs text-primary hover:underline mt-2"
            >
              Taking too long? Tap to refresh
            </button>
          </div>
        )}
        
        {/* Square SDK attaches to this div - must always be in DOM */}
        <div 
          id="square-card-container" 
          ref={cardContainerRef}
          className={`min-h-[50px] ${loading ? 'opacity-0 h-0 overflow-hidden' : ''}`}
        />
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2 text-red-700 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
          <button 
            onClick={() => window.location.reload()}
            className="mt-2 w-full py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg text-sm font-medium"
          >
            Refresh Page & Try Again
          </button>
        </div>
      )}

      {/* Terms Required Message */}
      {disabled && !loading && !error && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-center gap-2 text-amber-700 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>Please accept the Terms of Service and Payment Policy to continue</span>
          </div>
        </div>
      )}

      {/* Authorization Hold Notice */}
      {requiresAuthHold && !loading && !error && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
          <div className="flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-blue-900 mb-1">Enhanced Security Verification</p>
              <p className="text-xs text-blue-700">
                For orders between $5,000 - $15,000, we place an authorization hold on your card. 
                The charge will be processed within 1-2 business days after verification. 
                You'll receive an email once your payment is confirmed.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Pay Button */}
      <Button
        onClick={handlePayment}
        disabled={processing || disabled || !card}
        className="w-full bg-primary hover:bg-primary/90 text-white py-6 text-lg font-semibold"
        data-testid="square-pay-btn"
      >
        {processing ? (
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            {requiresAuthHold ? 'Authorizing...' : 'Processing...'}
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Lock className="w-5 h-5" />
            {requiresAuthHold 
              ? `Authorize $${amount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}`
              : `Pay $${amount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}`
            }
          </div>
        )}
      </Button>

      {/* Security Badge */}
      <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
        <Lock className="w-3 h-3" />
        <span>Secured by Square · PCI DSS Compliant · 3D Secure Enabled</span>
      </div>
    </div>
  );
}
